import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'

import { useTheme, Theme } from '../../theme'
import CatchGlobalKey from '../../utils/catch-global-key'
import { VisibilityProvider } from '../../utils/visibility-fade'
import { isKey, isElement } from '../../utils'

import { Flex } from '../flex'
import { GlobalErrorBoundary, IGlobalErrorBoundaryProps } from '../global-error-boundary'

import getStyles from './styles'

function ModalErrorBoundary(props: IGlobalErrorBoundaryProps) {
  const theme = useTheme()
  const styles = getStyles(theme)

  if (process.env.NODE_ENV === 'test') {
    return <>{props.children}</>
  }

  return (
    <GlobalErrorBoundary
      {...props}
      style={{
        padding: theme.settings.spacing.xxLarge,
        backgroundColor: theme.colors.backgrounds.main,
        boxShadow: theme.settings.shadows.regular,
        ...styles.modal.base,
        ...styles.modal.visible,
      }}
    />
  )
}

type ModalVariant = 'primary' | 'secondary'
const variants: ModalVariant[] = ['primary', 'secondary']

const cloneModalChild = (child: React.ReactNode, hasTopbar: boolean, variant?: ModalVariant) => {
  if (!child) return null

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return isElement(child) && (child.type === ModalHeader || child.type === ModalTopbar)
    ? React.cloneElement(child, { hasTopbar, variant })
    : child
}

function isRefObject(node: any): node is { current: HTMLElement } {
  return Boolean(node && node.current && typeof node.current.contains === 'function')
}

interface IModalProps {
  children: React.ReactNode
  onClose?: () => void
  innerRef?: React.RefObject<HTMLElement | HTMLDivElement> | ((ref: HTMLElement | HTMLDivElement) => void)
  variant?: ModalVariant
  fixedHeight?: boolean
  centered?: boolean
  style?: Rule
  hasCloseButton?: boolean
}
export class Modal extends React.Component<IModalProps> {
  static propTypes = {
    children: PropTypes.node.isRequired,
    onClose: PropTypes.func,
    innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object] as any),
    variant: PropTypes.oneOf(variants),
    fixedHeight: PropTypes.bool,
    centered: PropTypes.bool,
    style: PropTypes.object,
    hasCloseButton: PropTypes.bool,
  }
  static defaultProps = {
    variant: 'primary' as ModalVariant,
    hasCloseButton: true,
  }

  constructor(props: IModalProps) {
    super(props)

    this.contentElement = React.createRef()
  }

  componentDidMount() {
    if (isRefObject(this.contentElement) && !this.contentElement.current.contains(document.activeElement)) {
      this.contentElement.current.focus()
    }
  }

  attachRef = (el: HTMLElement | HTMLDivElement) => {
    ;(this.contentElement.current as any) = el

    if (this.props.innerRef && typeof this.props.innerRef === 'function') {
      this.props.innerRef(el)
    } else if (this.props.innerRef) {
      ;(this.props.innerRef.current as any) = el
    }
  }

  contentElement: React.RefObject<HTMLElement | HTMLDivElement>

  render() {
    const { children, style, hasCloseButton, onClose, variant, fixedHeight, centered } = this.props

    const hasTopbar = React.Children.toArray(children)
      .map(child => child && isElement(child) && child.type)
      .some(
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        type => type === ModalTopbar
      )

    return (
      <ModalErrorBoundary>
        <Theme>
          {theme => {
            const styles = getStyles(theme)
            return (
              <VisibilityProvider>
                {({ visible }) => (
                  <CatchGlobalKey keyType="escape" onKey={onClose}>
                    <Flex
                      component="article"
                      innerRef={this.attachRef}
                      role="dialog"
                      style={[
                        styles.modal.base,
                        visible && styles.modal.visible,
                        fixedHeight && styles.modal.fixedHeight,
                        centered && styles.modal.centered,
                        style,
                      ]}
                      tabIndex={0}
                    >
                      {React.Children.map(children, child => cloneModalChild(child, hasTopbar, variant))}

                      {onClose && hasCloseButton && (
                        <ModalCloseButton hasTopbar={hasTopbar} onClick={onClose} variant={variant} />
                      )}
                    </Flex>
                  </CatchGlobalKey>
                )}
              </VisibilityProvider>
            )
          }}
        </Theme>
      </ModalErrorBoundary>
    )
  }
}

interface IModalTopbarButtonProps {
  children: React.ReactNode
  disabled?: boolean
  innerRef?: React.RefObject<HTMLButtonElement>
  onClick?: () => void
  style?: Rule
  variant?: ModalVariant
}
type ExtendedModalTopbarButtonProps = IModalTopbarButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>

export const ModalTopbarButton = React.forwardRef<HTMLButtonElement, ExtendedModalTopbarButtonProps>(function(
  { children, disabled, onClick, style, variant = 'primary', innerRef, ...rest }: ExtendedModalTopbarButtonProps,
  ref
) {
  const styles = getStyles(useTheme())

  return (
    <button
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      {...css([
        styles.topbarButton.base,
        isKey(styles.topbarButton.variants, variant) && styles.topbarButton.variants[variant],
        style,
      ])}
      ref={innerRef || ref}
      {...rest}
    >
      {children}
    </button>
  )
})

export function ModalTopbarSpacer() {
  return <span {...css({ marginRight: 'auto' })} />
}

interface IModalCloseButtonProps {
  hasTopbar?: boolean
  variant?: ModalVariant
  onClick?: () => void
}
function ModalCloseButton({ hasTopbar, onClick, variant }: IModalCloseButtonProps) {
  const styles = getStyles(useTheme())
  return (
    <button
      onClick={onClick}
      {...css([
        styles.topbarButton.base,
        styles.closeButton.base,
        hasTopbar ? styles.closeButton.hasTopbar : null,
        isKey(styles.topbarButton.variants, variant) && styles.topbarButton.variants[variant],
      ])}
      type="button"
    >
      <svg height="30" viewBox="0 0 30 30" width="30">
        <path
          d="M23,15.1039547 C23,14.5556223 22.5551682,14.1111111 21.9962337,14.1111111 L15.8888889,14.1111111 L15.8888889,8.00376627 C15.8888889,7.44940147 15.4402805,7 14.8960453,7 L15.1039547,7 C14.5556223,7 14.1111111,7.44483185 14.1111111,8.00376627 L14.1111111,14.1111111 L8.00376627,14.1111111 C7.44940147,14.1111111 7,14.5597195 7,15.1039547 L7,14.8960453 C7,15.4443777 7.44483185,15.8888889 8.00376627,15.8888889 L14.1111111,15.8888889 L14.1111111,21.9962337 C14.1111111,22.5505985 14.5597195,23 15.1039547,23 L14.8960453,23 C15.4443777,23 15.8888889,22.5551682 15.8888889,21.9962337 L15.8888889,15.8888889 L21.9962337,15.8888889 C22.5505985,15.8888889 23,15.4402805 23,14.8960453 L23,15.1039547 Z"
          fill="white"
          transform="translate(15.000000, 15.000000) rotate(-315.000000) translate(-15.000000, -15.000000)"
        />
      </svg>
    </button>
  )
}

ModalCloseButton.propTypes = {
  hasTopbar: PropTypes.bool,
  variant: PropTypes.oneOf(variants),
  onClick: PropTypes.func,
}

interface IModalTopbarProps {
  children?: React.ReactNode
  variant?: ModalVariant
  style?: Rule
}
export function ModalTopbar({ children, variant, style }: IModalTopbarProps) {
  const styles = getStyles(useTheme())
  return (
    <Flex
      alignItems="center"
      flexShrink={0}
      row
      style={[styles.topbar.base, isKey(styles.topbar.variants, variant) && styles.topbar.variants[variant], style]}
    >
      {children}
    </Flex>
  )
}
ModalTopbar.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(variants),
  style: PropTypes.object,
}

interface IModalHeaderProps {
  children: React.ReactNode
  hasTopbar?: boolean
  variant?: ModalVariant
  style?: Rule
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'
}

/**
 * @type {React.StatelessComponent}
 */
export function ModalHeader({ children, style, variant, hasTopbar = false, justifyContent }: IModalHeaderProps) {
  const styles = getStyles(useTheme())
  return (
    <Flex
      alignItems="center"
      component="header"
      flexShrink={0}
      justifyContent={justifyContent}
      row
      style={[
        styles.header.base,
        hasTopbar && styles.header.hasTopbar,
        isKey(styles.header.variants, variant) && styles.header.variants[variant],
        style,
      ]}
    >
      {children}
    </Flex>
  )
}

ModalHeader.propTypes = {
  hasTopbar: PropTypes.bool,
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(variants),
  style: PropTypes.object,
  justifyContent: PropTypes.oneOf([
    'flex-start',
    'flex-end',
    'center',
    'space-between',
    'space-around',
    'space-evenly',
  ]),
}

interface IModalContentProps {
  children: React.ReactNode
  style?: Rule
  tight?: boolean
  flush?: boolean
  innerRef?: React.RefObject<HTMLElement | HTMLDivElement> | ((ref: HTMLElement) => void)
}

/**
 * @type {React.StatelessComponent}
 */
export function ModalContent({ children, style, tight, flush, innerRef }: IModalContentProps) {
  const styles = getStyles(useTheme())
  return (
    <Flex
      flexShrink={1}
      innerRef={innerRef}
      style={[styles.content.base, tight && styles.content.tight, flush && styles.content.flush, style]}
    >
      {children}
    </Flex>
  )
}

ModalContent.propTypes = {
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
  tight: PropTypes.bool,
  flush: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
}

interface IModalFooterProps {
  children: React.ReactNode
  style?: Rule
  tight?: boolean
  flush?: boolean
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'
}

/**
 * @type {React.StatelessComponent}
 */
export function ModalFooter({ children, style, tight, flush, justifyContent = 'flex-end' }: IModalFooterProps) {
  const styles = getStyles(useTheme())
  return (
    <Flex
      component="footer"
      flexShrink={0}
      justifyContent={justifyContent}
      row
      style={[styles.footer.base, tight && styles.footer.tight, flush && styles.footer.flush, style]}
    >
      {children}
    </Flex>
  )
}

ModalFooter.propTypes = {
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
  tight: PropTypes.bool,
  flush: PropTypes.bool,
  justifyContent: PropTypes.oneOf([
    'flex-start',
    'flex-end',
    'center',
    'space-between',
    'space-around',
    'space-evenly',
  ]),
}
